/*
Based on Material Dashboard 2 React by Creative Tim (https://www.creative-tim.com)
Licensed under the MIT License (see LICENSE)

Original Copyright (c) 2013-2021 Creative Tim
Modifications (c) 2025 CodeWeave Solutions (Pty) Ltd
*/

import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/FD_background.png";
import { buildApiUrl, API_ENDPOINTS } from '../../../config/api';
import fetchWithAuth from '../../../utils/fetchWithAuth';

// Constants
const STORAGE_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refresh_token',
  REMEMBERED_EMAIL: 'rememberedEmail',
};

const ERROR_MESSAGES = {
  SIGN_IN_FAILED: 'Invalid email or password',
  GENERIC_ERROR: 'Invalid email or password',
  INVALID_CREDENTIALS: 'Invalid email or password',
  NETWORK_ERROR: 'Unable to connect. Please check your internet connection and try again.',
  SERVER_ERROR: 'Invalid email or password',
  INVALID_RESPONSE: 'Invalid email or password',
  EMAIL_REQUIRED: 'Email is required',
  PASSWORD_REQUIRED: 'Password is required',
  INVALID_EMAIL: 'Please enter a valid email address',
  PASSWORD_TOO_SHORT: 'Password must be at least 6 characters long',
};

const VALIDATION_RULES = {
  MIN_PASSWORD_LENGTH: 6,
};

const ROUTES = {
  CLIENTS: '/clients',
  RESET_PASSWORD: '/authentication/reset-password',
  LOGIN_REQUEST: '/authentication/login-request',
};

// Styles
const CARD_STYLES = {
  backdropFilter: "blur(5px)",
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
  borderRadius: "16px",
  border: "1px solid rgba(255, 255, 255, 0.18)",
  transition: "transform 0.3s ease-in-out",
  width: "100%",
  maxWidth: "400px",
  margin: "0 auto",
  "&:hover": {
    transform: "translateY(-5px)"
  }
};

const HEADER_STYLES = {
  background: "linear-gradient(45deg, #4A90E2 30%, #2C5282 90%)",
  boxShadow: "0 3px 5px 2px rgba(74, 144, 226, .2)"
};

const INPUT_STYLES = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: "info.main",
    },
  },
};

const CHECKBOX_STYLES = {
  color: "info.main",
  "&.Mui-checked": {
    color: "info.main",
  },
};

const LINK_STYLES = {
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
};

const ERROR_BOX_STYLES = {
  backgroundColor: "rgba(211, 47, 47, 0.1)",
  borderRadius: "8px",
  border: "1px solid rgba(211, 47, 47, 0.2)",
};

const SUBMIT_BUTTON_STYLES = {
  height: "48px",
  background: "linear-gradient(45deg, #4A90E2 30%, #2C5282 90%)",
  boxShadow: "0 3px 5px 2px rgba(74, 144, 226, .2)",
  "&:hover": {
    background: "linear-gradient(45deg, #2C5282 30%, #4A90E2 90%)",
  },
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if email is valid
 */
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

/**
 * Main SignIn component
 */
export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  /**
   * Handle error response parsing
   * @param {Response} response - The fetch response
   * @returns {Promise<string>} Error message
   */
  const handleErrorResponse = useCallback(async (response) => {
    // Always return generic error message for security
    // Don't expose specific error details to prevent information disclosure
    console.warn('Sign-in failed with status:', response.status);
    return ERROR_MESSAGES.INVALID_CREDENTIALS;
  }, []);

  /**
   * Parse and validate successful response
   * @param {Response} response - The fetch response
   * @returns {Promise<Object>} Parsed response data
   */
  const parseSuccessResponse = useCallback(async (response) => {
    const contentType = response.headers.get('content-type');
    const contentLength = response.headers.get('content-length');
    
    if (contentLength === '0' || !contentType) {
      throw new Error(ERROR_MESSAGES.SERVER_ERROR);
    }
    
    if (!contentType.includes('application/json')) {
      console.warn('Unexpected content type:', contentType);
      throw new Error(ERROR_MESSAGES.INVALID_RESPONSE);
    }
    
    let data;
    try {
      data = await response.json();
      
      if (!data || typeof data !== 'object') {
        throw new Error(ERROR_MESSAGES.INVALID_RESPONSE);
      }
      
      if (!data.access_token) {
        throw new Error(ERROR_MESSAGES.INVALID_RESPONSE);
      }
    } catch (parseError) {
      console.error('Failed to parse response as JSON:', parseError);
      throw new Error(ERROR_MESSAGES.INVALID_RESPONSE);
    }
    
    return data;
  }, []);

  /**
   * Store authentication tokens and user preferences
   * @param {Object} data - Response data containing tokens
   */
  const storeAuthData = useCallback((data) => {
    localStorage.setItem(STORAGE_KEYS.TOKEN, data.access_token);
    
    // Validate refresh token before storing
    if (data.refresh_token && typeof data.refresh_token === 'string' && data.refresh_token.split('.').length === 3) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
    } else {
      // Ensure we don't persist an invalid value like "undefined"
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    }
    
    if (rememberMe) {
      localStorage.setItem(STORAGE_KEYS.REMEMBERED_EMAIL, email);
    } else {
      localStorage.removeItem(STORAGE_KEYS.REMEMBERED_EMAIL);
    }
  }, [email, rememberMe]);

  /**
   * Make sign-in request with fallback
   * @returns {Promise<Response>} The fetch response
   */
  const makeSignInRequest = useCallback(async () => {
    console.log('Attempting sign-in request to:', buildApiUrl(API_ENDPOINTS.SIGN_IN));
    
    try {
      return await fetchWithAuth(API_ENDPOINTS.SIGN_IN, {
        method: "POST", 
        body: JSON.stringify({ email, password }),
      });
    } catch (fetchError) {
      console.warn('fetchWithAuth failed, trying direct fetch:', fetchError);
      return await fetch(buildApiUrl(API_ENDPOINTS.SIGN_IN), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });
    }
  }, [email, password]);

  /**
   * Validate form inputs
   * @returns {boolean} True if validation passes
   */
  const validateForm = useCallback(() => {
    if (!email.trim()) {
      setError(ERROR_MESSAGES.EMAIL_REQUIRED);
      return false;
    }
    
    if (!password.trim()) {
      setError(ERROR_MESSAGES.PASSWORD_REQUIRED);
      return false;
    }
    
    if (!isValidEmail(email.trim())) {
      setError(ERROR_MESSAGES.INVALID_EMAIL);
      return false;
    }
    
    if (password.length < VALIDATION_RULES.MIN_PASSWORD_LENGTH) {
      setError(ERROR_MESSAGES.PASSWORD_TOO_SHORT);
      return false;
    }
    
    return true;
  }, [email, password]);

  /**
   * Handle form submission
   * @param {Event} e - Form submission event
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (loading) return;
    
    setError("");
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await makeSignInRequest();
      
      console.log('Sign-in response status:', response.status);
      console.log('Sign-in response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorMessage = await handleErrorResponse(response);
        throw new Error(errorMessage);
      }
      
      const data = await parseSuccessResponse(response);
      storeAuthData(data);
      navigate(ROUTES.CLIENTS, { replace: true });
    } catch (err) {
      console.error("Sign in error:", err);
      // Always show generic error message for security
      const errorMessage = err.name === 'TypeError' 
        ? ERROR_MESSAGES.NETWORK_ERROR 
        : ERROR_MESSAGES.INVALID_CREDENTIALS;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [loading, validateForm, makeSignInRequest, handleErrorResponse, parseSuccessResponse, storeAuthData, navigate]);

  const handleEmailChange = useCallback((e) => setEmail(e.target.value), []);
  const handlePasswordChange = useCallback((e) => setPassword(e.target.value), []);
  const handleRememberMeChange = useCallback((e) => setRememberMe(e.target.checked), []);
  const togglePasswordVisibility = useCallback(() => setShowPassword((show) => !show), []);

  return (
    <BasicLayout image={bgImage}>
      <Card sx={CARD_STYLES}>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
          sx={HEADER_STYLES}
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Welcome Back
          </MDTypography>
          <MDTypography variant="body2" color="white" opacity={0.8} mt={1}>
            Please sign in to continue
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                value={email}
                onChange={handleEmailChange}
                required
                disabled={loading}
                sx={INPUT_STYLES}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type={showPassword ? "text" : "password"}
                label="Password"
                fullWidth
                value={password}
                onChange={handlePasswordChange}
                required
                disabled={loading}
                sx={INPUT_STYLES}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                        size="small"
                        sx={{ color: "info.main" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    sx={CHECKBOX_STYLES}
                  />
                }
                label={
                  <MDTypography variant="button" fontWeight="regular" color="text">
                    Remember me
                  </MDTypography>
                }
              />
              <MDTypography
                component={Link}
                to={ROUTES.RESET_PASSWORD}
                variant="button"
                color="info"
                fontWeight="medium"
                textGradient
                sx={LINK_STYLES}
              >
                Forgot Password?
              </MDTypography>
            </MDBox>
            {error && (
              <MDBox 
                mb={2} 
                p={1.5} 
                sx={ERROR_BOX_STYLES}
              >
                <MDTypography variant="caption" color="error" fontWeight="medium">
                  {error}
                </MDTypography>
              </MDBox>
            )}
            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                type="submit"
                disabled={loading}
                sx={SUBMIT_BUTTON_STYLES}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Sign In"
                )}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Don&apos;t have an account?{" "}
                <MDTypography
                  component={Link}
                  to={ROUTES.LOGIN_REQUEST}
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                  sx={LINK_STYLES}
                >
                  Request Access
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

// PropTypes for type safety
SignIn.propTypes = {
  // Add props here if component accepts any
};
